import type {
  DraftEdit,
  DraftFields,
  NewDraftPhoto,
  RemovalTombstone,
  UnsyncedDraft,
  UnsyncedPhoto,
} from '../../domain/drafts';
import { MAX_DRAFT_PHOTOS, TooManyPhotosError } from '../../domain/drafts';

type Listener = () => void;

/** Where a draft was made: its batch's moment and its place among every draft made here. */
export type DraftPlace = { createdAt: number; seq: number };

/** What the engine records about a draft as answers come back; never the person's content. */
export type SyncRecord = Partial<
  Pick<
    UnsyncedDraft,
    'sentRevision' | 'serverConfirmed' | 'serverPhotoIds' | 'blocker' | 'nextAttemptAt'
  >
>;

/**
 * The page's unsynced drafts (contracts/clients.md `DraftOutbox`). Memory
 * only — never browser storage (FR-007a) — so it lasts exactly as long as the
 * page. Every change swaps in new objects, so a snapshot from `drafts()` can
 * be handed to React as is (`useSyncExternalStore`).
 *
 * The person's actions (`createDrafts`, `save`, `remove`) also notify the
 * local-change listeners that wake the sync engine; the engine's own writes
 * (`record*`, `settle`, `dropTombstone`) don't, so they can't wake it in a
 * loop. Engine writes to a draft that has since been removed are dropped,
 * which is half of "removal wins" (FR-009c); the tombstone is the other half.
 */
export class DraftOutbox {
  private rows: readonly UnsyncedDraft[] = [];
  private removals: readonly RemovalTombstone[] = [];
  /** Kept after a draft syncs, so lists can keep a batch in the order it was made. */
  private readonly places = new Map<string, DraftPlace>();
  private readonly listeners = new Set<Listener>();
  private readonly localListeners = new Set<Listener>();
  private readonly newId: () => string;
  private readonly now: () => number;

  constructor(
    readonly accountId: string,
    {
      newId = () => crypto.randomUUID(),
      now = Date.now,
    }: { newId?: () => string; now?: () => number } = {},
  ) {
    this.newId = newId;
    this.now = now;
  }

  // ── Reading ───────────────────────────────────────────────────────────

  /** Oldest first. The same array until something changes. */
  drafts(): readonly UnsyncedDraft[] {
    return this.rows;
  }

  get(id: string): UnsyncedDraft | undefined {
    return this.rows.find((d) => d.id === id);
  }

  tombstones(): readonly RemovalTombstone[] {
    return this.removals;
  }

  /** Where a draft made on this page was made, synced or not; undefined for any other. */
  placeOf(id: string): DraftPlace | undefined {
    return this.places.get(id);
  }

  /** Whether leaving the page now would lose a draft or strand a removal. */
  hasPendingWork(): boolean {
    return this.rows.length > 0 || this.removals.length > 0;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeLocalChanges(listener: Listener): () => void {
    this.localListeners.add(listener);
    return () => this.localListeners.delete(listener);
  }

  // ── The person's actions ──────────────────────────────────────────────

  /** One draft per item, all or none (FR-006). Each item holds 1–20 photos. */
  createDrafts(defaults: DraftFields, items: NewDraftPhoto[][]): UnsyncedDraft[] {
    for (const item of items) {
      if (item.length > MAX_DRAFT_PHOTOS) throw new TooManyPhotosError();
      if (item.length === 0) throw new Error('A draft needs at least one photo.');
    }
    // Built in full before anything is stored, so a failure part-way stores nothing.
    const createdAt = this.now();
    const made = items.map((item) => ({
      id: this.newId(),
      accountId: this.accountId,
      createdAt,
      fields: { ...defaults },
      photos: item.map((p) => this.newPhoto(p)),
      localRevision: 1,
      sentRevision: null,
      serverConfirmed: false,
      serverPhotoIds: [],
      blocker: null,
      nextAttemptAt: null,
    }));
    this.rows = [...this.rows, ...made];
    for (const draft of made) this.places.set(draft.id, { createdAt, seq: this.places.size });
    this.changed(true);
    return made;
  }

  /**
   * Save an edit. Bumps the revision so sync carries it; clears a pending
   * backoff so it goes out promptly, and "Choose another collection" when a
   * collection is chosen.
   */
  save(id: string, edit: DraftEdit): UnsyncedDraft {
    const draft = this.get(id);
    if (!draft) throw new Error('This draft is gone.');
    const fields = { ...draft.fields, ...edit.fields };
    const collectionChanged = fields.collectionId !== draft.fields.collectionId;
    if (collectionChanged && draft.serverConfirmed) {
      throw new Error('A draft stays in its collection once it has synced.');
    }

    let photos = draft.photos;
    if (edit.photoIds) {
      photos = edit.photoIds.map((photoId) => {
        const kept = draft.photos.find((p) => p.id === photoId);
        if (!kept) throw new Error('That photo is not on this draft.');
        return kept;
      });
    }
    if (edit.addPhotos) photos = [...photos, ...edit.addPhotos.map((p) => this.newPhoto(p))];
    if (photos.length > MAX_DRAFT_PHOTOS) throw new TooManyPhotosError();

    const saved: UnsyncedDraft = {
      ...draft,
      fields,
      photos,
      localRevision: draft.localRevision + 1,
      nextAttemptAt: null,
      blocker: collectionChanged && draft.blocker === 'chooseCollection' ? null : draft.blocker,
    };
    this.replace(saved);
    this.changed(true);
    return saved;
  }

  /** Gone from the page at once; the tombstone tells sync to delete any server copy. */
  remove(id: string): void {
    const draft = this.get(id);
    if (!draft) return;
    this.rows = this.rows.filter((d) => d.id !== id);
    this.removals = [...this.removals, { draftId: id, photoIds: draft.photos.map((p) => p.id) }];
    this.changed(true);
  }

  /** Sign-out: drop everything, without a word (FR-009d). */
  purge(): void {
    this.rows = [];
    this.removals = [];
    this.places.clear();
    this.changed(false);
  }

  // ── The engine's records ──────────────────────────────────────────────

  record(id: string, changes: SyncRecord): void {
    const draft = this.get(id);
    if (!draft) return;
    this.replace({ ...draft, ...changes });
    this.changed(false);
  }

  /** The same record on every draft (an account-wide pause or backoff). */
  recordAll(changes: (draft: UnsyncedDraft) => SyncRecord): void {
    if (this.rows.length === 0) return;
    this.rows = this.rows.map((d) => ({ ...d, ...changes(d) }));
    this.changed(false);
  }

  recordPhoto(
    id: string,
    photoId: string,
    changes: Partial<Pick<UnsyncedPhoto, 'uploaded' | 'failure'>>,
  ): void {
    const draft = this.get(id);
    if (!draft || !draft.photos.some((p) => p.id === photoId)) return;
    this.replace({
      ...draft,
      photos: draft.photos.map((p) => (p.id === photoId ? { ...p, ...changes } : p)),
    });
    this.changed(false);
  }

  /** The service holds the draft as the device has it: it leaves the outbox. */
  settle(id: string): void {
    if (!this.get(id)) return;
    this.rows = this.rows.filter((d) => d.id !== id);
    this.changed(false);
  }

  dropTombstone(draftId: string): void {
    this.removals = this.removals.filter((t) => t.draftId !== draftId);
    this.changed(false);
  }

  // ── Machinery ─────────────────────────────────────────────────────────

  private newPhoto({ file, width, height }: NewDraftPhoto): UnsyncedPhoto {
    return { id: this.newId(), file, width, height, uploaded: false, failure: null };
  }

  private replace(draft: UnsyncedDraft): void {
    this.rows = this.rows.map((d) => (d.id === draft.id ? draft : d));
  }

  private changed(byPerson: boolean): void {
    for (const listener of [...this.listeners]) listener();
    if (byPerson) for (const listener of [...this.localListeners]) listener();
  }
}
